
import React from 'react';
import axios from 'axios';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { getGuard } from './unsavedGuard';

// --- UTM PARAM CAPTURE: Store UTM params in sessionStorage on first load ---
(() => {
  if (typeof window !== 'undefined' && window.location && window.sessionStorage) {
    const url = new URL(window.location.href);
    const utmKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
    ];
    let found = false;
    utmKeys.forEach((key) => {
      const value = url.searchParams.get(key);
      if (value !== null) {
        window.sessionStorage.setItem(key, value);
        found = true;
      }
    });
    // Optionally, store the landing page URL with UTMs for analytics
    if (found) {
      window.sessionStorage.setItem('utm_landing_url', window.location.href);
    }
  }
})();

// Diagnostic global listeners to verify browser navigation events reach the SPA
// Navigation interception at top-level: consult registered guard before allowing back/toolbar navigation.
(() => {
  const originalPush = window.history.pushState;
  const originalReplace = window.history.replaceState;
  const originalBack = window.history.back;
  const originalGo = window.history.go;
  let prevHref = window.location.href;
  let suppressNext = false;

  function applyNav(method, args) {
    try { method.apply(window.history, args); } catch (e) { /* ignore */ }
    // update prevHref after navigation
    prevHref = window.location.href;
  }

  async function handleAttemptedNav(attemptedHref, doMethod) {
    const guard = getGuard();
    if (!guard || !guard.isDirty || !guard.isDirty()) {
      doMethod();
      return;
    }
    // revert to previous URL immediately
    try { originalPush.apply(window.history, [null, '', prevHref]); } catch (e) { window.history.pushState(null, '', prevHref); }
    suppressNext = true;
    try {
      const ok = guard.showPrompt ? await guard.showPrompt() : window.confirm('You have unsaved changes. Are you sure you want to leave without saving?');
      if (ok) {
        // allow the navigation to proceed by invoking the original method
        suppressNext = true;
        doMethod();
        // ensure router notices the new URL (some routers don't react to pushState without popstate)
        try { window.dispatchEvent(new PopStateEvent('popstate')); } catch (e) { /* ignore */ }
        // update prevHref after navigation
        prevHref = window.location.href;
      }
    } catch (e) {
      /* swallow */
    }
  }

  window.history.pushState = function (...args) {
    handleAttemptedNav(args[2] || window.location.href, () => applyNav(originalPush, args));
  };
  window.history.replaceState = function (...args) {
    handleAttemptedNav(args[2] || window.location.href, () => applyNav(originalReplace, args));
  };

  window.history.back = function () {
    handleAttemptedNav(document.referrer || prevHref, () => { try { originalBack.apply(window.history); } catch (e) { try { window.history.back(); } catch (_) { } } });
  };
  window.history.go = function (delta) {
    handleAttemptedNav(document.referrer || prevHref, () => { try { originalGo.apply(window.history, [delta]); } catch (e) { try { window.history.go(delta); } catch (_) { } } });
  };

  window.addEventListener('popstate', (e) => {
    if (suppressNext) { suppressNext = false; prevHref = window.location.href; return; }
    // when popstate happens (toolbar back), attempt to block if guard is dirty
    const attempted = window.location.href;
    const guard = getGuard();
    if (guard && guard.isDirty && guard.isDirty()) {
      // revert
      try { originalPush.apply(window.history, [null, '', prevHref]); } catch (err) { window.history.pushState(null, '', prevHref); }
      suppressNext = true;
      (async () => {
        try {
          const ok = guard.showPrompt ? await guard.showPrompt() : window.confirm('You have unsaved changes. Are you sure you want to leave without saving?');
          if (ok) {
            suppressNext = true;
            try { originalBack.apply(window.history); } catch (e) { try { window.history.back(); } catch (_) { } }
          }
        } catch (err) { /* swallow */ }
      })();
    } else {
      prevHref = attempted;
    }
  });
})();
window.addEventListener('hashchange', (e) => {
});

if (window.location.host.includes("localhost")) {
  axios.defaults.baseURL = 'http://localhost:8080';
} else {
  axios.defaults.baseURL = window.location.origin + '/rys-ws';
}

axios.defaults.headers.post['Content-Type'] = 'application/json';
// Send cookies (session) with requests so server-side sessions work
axios.defaults.withCredentials = true;
// axios.defaults.headers.common['Authorization'] = `Bearer ${Cookies.getJwtToken()}`;

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
